import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Image as ImageIcon, FileSpreadsheet, Loader2 } from "lucide-react";

/**
 * DescargaFormatoModal — pide al usuario si quiere PDF, Imagen (PNG) o Excel (xlsx).
 *
 * Props:
 *   open, onOpenChange: control del Dialog
 *   titulo?: string       - encabezado del modal (default "¿Cómo lo descargas?")
 *   onElegir: (format: "pdf" | "png" | "xlsx") => Promise|void
 *   habilitarXlsx?: bool  - default true. Muestra la opción de Excel.
 *
 * Mientras `onElegir` está en curso, deshabilita los botones y evita cerrar el modal.
 */
export default function DescargaFormatoModal({ open, onOpenChange, titulo = "¿Cómo lo descargas?", onElegir, habilitarXlsx = true }) {
  const [saving, setSaving] = useState(false);

  const handle = async (format) => {
    setSaving(true);
    try {
      await onElegir?.(format);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (saving) return; onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="descarga-formato-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{titulo}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Elige el formato que prefieras. PDF para imprimir, imagen para WhatsApp, Excel para trabajo interno.
          </p>
        </DialogHeader>
        <div className={`grid ${habilitarXlsx ? "grid-cols-3" : "grid-cols-2"} gap-3 mt-4`}>
          <Button
            onClick={() => handle("pdf")}
            disabled={saving}
            variant="outline"
            className="h-24 flex flex-col gap-2 border-2 hover:border-primary hover:bg-primary/5"
            data-testid="descarga-formato-pdf"
          >
            {saving ? <Loader2 className="w-8 h-8 animate-spin" /> : <FileText className="w-8 h-8 text-rose-600" />}
            <span className="font-semibold">PDF</span>
          </Button>
          <Button
            onClick={() => handle("png")}
            disabled={saving}
            variant="outline"
            className="h-24 flex flex-col gap-2 border-2 hover:border-primary hover:bg-primary/5"
            data-testid="descarga-formato-png"
          >
            {saving ? <Loader2 className="w-8 h-8 animate-spin" /> : <ImageIcon className="w-8 h-8 text-primary" />}
            <span className="font-semibold">Imagen (PNG)</span>
          </Button>
          {habilitarXlsx && (
            <Button
              onClick={() => handle("xlsx")}
              disabled={saving}
              variant="outline"
              className="h-24 flex flex-col gap-2 border-2 hover:border-primary hover:bg-primary/5"
              data-testid="descarga-formato-xlsx"
            >
              {saving ? <Loader2 className="w-8 h-8 animate-spin" /> : <FileSpreadsheet className="w-8 h-8 text-emerald-600" />}
              <span className="font-semibold">Excel</span>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
